import { useOrder } from "@utils/store/cart/useOrder";
import { getReservableDays, isSlotPast, slotAvailable, todayStr } from "@utils/func/deliverySlots";
import { useEffect, useMemo } from "react";
import { ScrollView, Text, XStack, YStack } from "tamagui";
import KioskIcon from "../KioskIcon";
import { kiosk } from "../kioskTheme";

const WD = ["日", "一", "二", "三", "四", "五", "六"];

/** 日期字串 → 星期幾 */
function weekday(dateStr: string): string {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return WD[new Date(y, mo - 1, d).getDay()];
}

/**
 * Kiosk 專屬配送時間選擇器（取代 GasShop 的 DeliverTimeCard）：
 * 橫向日期條 + 清楚的時段列，符合點餐機風格、文字顏色明確可見。
 * 共用 useOrder 的 deliverySlots / deliveryTime，CheckoutGuide 的驗證邏輯不變。
 */
const KioskTimePicker = () => {
  const { deliverySlots, deliveryTime, setDeliveryTime, isOpenNow, allowReservation } = useOrder();

  // 可選日期：API 有回、非整日公休、且至少一個可用時段（與入口閘門共用同一判斷）
  const days = useMemo(() => getReservableDays(deliverySlots), [deliverySlots]);

  // 目前可用的配送方式：立即僅營業中(isOpenNow)、預約僅瓦斯行開放預約(allowReservation)。
  const canScheduled = allowReservation;
  const canImmediate = isOpenNow;

  // 有效配送方式（correct-by-construction）：store 內選取的 type 若目前不可用，直接落到另一種可用方式來渲染，
  // 確保未開放預約時「絕不」顯示日期/時段區（不依賴 effect 是否已 commit）。
  const effectiveType: "scheduled" | "immediate" =
    deliveryTime.type === "scheduled" && !canScheduled
      ? "immediate"
      : deliveryTime.type === "immediate" && !canImmediate
        ? "scheduled"
        : deliveryTime.type;

  // 把修正後的 type 同步回 store（供 CheckoutGuide 驗證與後續結帳一致）
  useEffect(() => {
    if (deliveryTime.type !== effectiveType) {
      setDeliveryTime({ ...deliveryTime, type: effectiveType, date: null, slotId: null, slotSource: null, slotLabel: null });
    }
  }, [effectiveType]);

  // 預約模式下，若尚未選日期，預設選第一個可用日（僅在預約可用時）
  useEffect(() => {
    if (effectiveType === "scheduled" && canScheduled && !deliveryTime.date && days.length > 0) {
      setDeliveryTime({ ...deliveryTime, date: days[0], slotId: null, slotSource: null, slotLabel: null });
    }
    // 只在可選日清單 / 有效方式變動時補預設
  }, [days, effectiveType]);

  const selectedDay = (deliverySlots || []).find((d) => d.delivery_date === deliveryTime.date) || null;

  // 配送方式選項：預約僅在瓦斯行開放預約時出現；立即僅在營業中出現。
  const deliveryOptions = [
    ...(canScheduled ? [{ t: "scheduled" as const, label: "預約配送", sub: "指定日期與時段" }] : []),
    ...(canImmediate ? [{ t: "immediate" as const, label: "立即配送", sub: "將儘速為您配送" }] : []),
  ];

  const setType = (type: "scheduled" | "immediate") =>
    setDeliveryTime({ ...deliveryTime, type, date: null, slotId: null, slotSource: null, slotLabel: null });
  const pickDate = (date: string) => setDeliveryTime({ ...deliveryTime, date, slotId: null, slotSource: null, slotLabel: null });
  const pickSlot = (s: any) =>
    setDeliveryTime({ ...deliveryTime, slotId: s.slot_id, slotSource: s.slot_source, slotLabel: `${s.start_time} - ${s.end_time}` });

  return (
    <YStack gap={16}>
      {/* 配送方式 */}
      <XStack gap={11}>
        {deliveryOptions.map((o) => {
          const sel = effectiveType === o.t;
          return (
            <YStack
              key={o.t}
              flex={1}
              ai="center"
              py={16}
              borderRadius={16}
              borderWidth={2}
              borderColor={sel ? kiosk.blue : kiosk.line}
              backgroundColor={sel ? kiosk.pillBg : "#fff"}
              cursor="pointer"
              onPress={() => setType(o.t)}
              pressStyle={{ scale: 0.97 }}>
              <Text fontSize={15} fontWeight="800" color={sel ? kiosk.blue : "#33405c"}>
                {o.label}
              </Text>
              <Text fontSize={11} color={kiosk.grey} mt={3}>
                {o.sub}
              </Text>
            </YStack>
          );
        })}
      </XStack>

      {effectiveType === "immediate" ? (
        <YStack backgroundColor="#f4f6fb" borderRadius={14} p={16} ai="center" gap={6}>
          <KioskIcon name="clock" size={34} color={kiosk.blue} />
          <Text fontSize={14} fontWeight="700" color={kiosk.ink}>
            下單後將儘速為您安排配送人員
          </Text>
          <Text fontSize={12} color={kiosk.grey} textAlign="center">
            有任何問題可以聯絡瓦斯行
          </Text>
        </YStack>
      ) : (
        <>
          {/* 目前可選範圍的年月（跨月時依所選日期更新） */}
          {(() => {
            const ref = deliveryTime.date || days[0] || "";
            if (!ref) return null;
            return (
              <Text fontSize={13} fontWeight="700" color={kiosk.ink}>
                {ref.slice(0, 4)} 年 {Number(ref.slice(5, 7))} 月
              </Text>
            );
          })()}
          {/* 橫向日期條 */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} className="kiosk-noscroll">
            <XStack gap={8} pb={2}>
              {days.map((d) => {
                const sel = deliveryTime.date === d;
                const isToday = d === todayStr();
                const dayNum = Number(d.split("-")[2]);
                return (
                  <YStack
                    key={d}
                    width={52}
                    py={9}
                    ai="center"
                    borderRadius={13}
                    borderWidth={2}
                    borderColor={sel ? kiosk.blue : kiosk.line}
                    backgroundColor={sel ? kiosk.blue : "#fff"}
                    cursor="pointer"
                    onPress={() => pickDate(d)}
                    pressStyle={{ scale: 0.93 }}>
                    <Text fontSize={11} color={sel ? "#fff" : kiosk.grey}>
                      {isToday ? "今天" : weekday(d)}
                    </Text>
                    <Text fontSize={18} fontWeight="800" mt={2} color={sel ? "#fff" : "#33405c"}>
                      {dayNum}
                    </Text>
                  </YStack>
                );
              })}
              {days.length === 0 && (
                <Text fontSize={13} color={kiosk.grey} py={10}>
                  目前無可預約日期
                </Text>
              )}
            </XStack>
          </ScrollView>

          {/* 時段 */}
          {deliveryTime.date && (
            <YStack gap={9}>
              {(selectedDay?.slots || []).map((s) => {
                const avail = slotAvailable(deliveryTime.date!, s);
                const sel = deliveryTime.slotId === s.slot_id;
                return (
                  <XStack
                    key={s.slot_id}
                    ai="center"
                    jc="space-between"
                    px={14}
                    py={14}
                    borderRadius={14}
                    borderWidth={2}
                    borderColor={sel ? kiosk.blue : kiosk.line}
                    backgroundColor={sel ? kiosk.pillBg : "#fff"}
                    opacity={avail ? 1 : 0.45}
                    cursor={avail ? "pointer" : "not-allowed"}
                    onPress={avail ? () => pickSlot(s) : undefined}
                    pressStyle={avail ? { scale: 0.98 } : undefined}>
                    <XStack ai="center" gap={9}>
                      <KioskIcon name="clock" size={18} color={sel ? kiosk.blue : kiosk.grey} />
                      <Text fontSize={15} fontWeight={sel ? "800" : "700"} color={sel ? kiosk.blue : "#33405c"}>
                        {s.start_time} - {s.end_time}
                      </Text>
                    </XStack>
                    <XStack ai="center" gap={8}>
                      <Text fontSize={12} color={avail ? kiosk.grey : kiosk.danger}>
                        {avail ? (s.available_capacity !== undefined ? `剩餘 ${s.available_capacity}` : "可預約") : isSlotPast(deliveryTime.date!, s.end_time) ? "已過時段" : "已額滿"}
                      </Text>
                      {sel && <KioskIcon name="check" size={16} color={kiosk.blue} strokeWidth={2.6} />}
                    </XStack>
                  </XStack>
                );
              })}
              {(!selectedDay?.slots || selectedDay.slots.length === 0) && (
                <Text fontSize={13} color={kiosk.grey} textAlign="center" py={6}>
                  當日無可選時段
                </Text>
              )}
            </YStack>
          )}
        </>
      )}
    </YStack>
  );
};

export default KioskTimePicker;
